import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { LucideIcon, Info, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface KPICardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  subtitle?: string;
  tooltip?: string;
  /** URL de navigation au clic (optionnel) */
  href?: string;
  /** Handler onClick custom (optionnel, ignoré si href est fourni) */
  onClick?: () => void;
}

const KPICard = ({ title, value, icon: Icon, trend, subtitle, tooltip, href, onClick }: KPICardProps) => {
  const isClickable = !!(href || onClick);

  // Contenu de la carte (réutilisé avec ou sans wrapper cliquable)
  const cardContent = (
    <Card className={cn(
      "card-premium hover-lift group animate-fade-up relative",
      isClickable && "cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-primary/30 hover:shadow-lg"
    )}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 px-4">
        <CardTitle className="text-xs font-semibold text-white uppercase tracking-wide">{title}</CardTitle>
        <div className="p-2 rounded-lg bg-muted/50 group-hover:bg-muted transition-smooth">
          <Icon className="h-4 w-4 text-muted-foreground group-hover:scale-110 transition-smooth" />
        </div>
      </CardHeader>
      <CardContent className="pb-4 px-4">
        <div className="text-3xl font-black tracking-tight font-mono bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
          {value}
        </div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1.5">{subtitle}</p>}
        {trend && (
          <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-md text-xs font-semibold ${
            trend.isPositive
              ? "bg-success/10 text-success"
              : "bg-danger/10 text-danger"
          }`}>
            <span className="text-sm">{trend.isPositive ? "↑" : "↓"}</span>
            {Math.abs(trend.value)}% vs mois dernier
          </div>
        )}
      </CardContent>
      {/* Indicateur cliquable (chevron) - affiché si href ou onClick */}
      {isClickable && (
        <div className="absolute bottom-3 right-3 p-1.5 rounded-full bg-muted/50 group-hover:bg-muted transition-colors">
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
        </div>
      )}
      {/* Tooltip info (décalé si clickable) */}
      {tooltip && (
        <HoverCard openDelay={200} closeDelay={100}>
          <HoverCardTrigger asChild>
            <button
              className={cn(
                "absolute p-1 rounded-full bg-muted/50 hover:bg-muted transition-colors cursor-help",
                isClickable ? "bottom-3 right-10" : "bottom-3 right-3"
              )}
              type="button"
              onClick={(e) => e.stopPropagation()}
            >
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </HoverCardTrigger>
          <HoverCardContent side="top" align="end" sideOffset={8} className="w-64 p-3">
            <p className="text-sm text-foreground">{tooltip}</p>
          </HoverCardContent>
        </HoverCard>
      )}
    </Card>
  );

  // Si href fourni, wrapper dans un Link
  if (href) {
    return (
      <Link to={href} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
        {cardContent}
      </Link>
    );
  }

  // Si onClick fourni, wrapper dans un div cliquable
  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl"
      >
        {cardContent}
      </div>
    );
  }

  // Sinon retourner la carte sans wrapper
  return cardContent;
};

export default KPICard;
